import { PublicClientApplication } from '@azure/msal-browser';
import Alpine from 'alpinejs';
import type { Client, Dashboard } from '../shared/types';
import type { DashboardContent, DashboardController } from './dashboard';
import { DEFAULT_DASHBOARD_CONTENT, initDashboard, isDashboardFormat, SaveError } from './dashboard';

window.Alpine = Alpine;

type DashboardUpdateBody = { programName?: string; content?: Record<string, unknown>; allowedUserEmails?: string[] };
type DashboardUpdateResult =
  | { ok: true; dashboard: Dashboard | null }
  | { ok: false; status: number; error: string; dashboard: Dashboard | null };

Alpine.data('app', () => ({
  // Auth
  currentUser: null as { id: string; email: string; name: string; isKaiUser: boolean } | null,
  initialLoading: true,

  // Login form
  loginEmail: '',
  loginPassword: '',
  loginError: null as string | null,
  loggingIn: false,

  // Microsoft SSO
  microsoftSsoEnabled: !!(process.env.MICROSOFT_CLIENT_ID),
  msalLoading: false,
  msalError: null as string | null,
  _msalPca: null as PublicClientApplication | null,

  // MFA
  mfaRequired: false,
  mfaSession: '',
  mfaMethod: '',
  mfaEmail: '',
  mfaCode: '',
  mfaError: null as string | null,
  mfaLoading: false,

  // Views: 'list' | 'detail' | 'new'
  view: 'list',

  // Clients
  clients: [] as Client[],
  clientsLoading: false,
  selectedClientId: '',

  // Dashboards
  dashboards: [] as Dashboard[],
  dashboardsLoading: false,

  // Detail
  selectedDashboard: null as Dashboard | null,

  // Edit mode (on detail view)
  editMode: false,
  editProgramName: '',
  editContent: '',
  editError: null as string | null,
  editSaving: false,
  editBlocked: false,

  // New dashboard form
  formProgramName: '',
  formClientId: '',
  formContent: '{}',
  formError: null as string | null,
  formSaving: false,

  // General error
  error: null as string | null,

  // Theme
  darkMode: false,

  // Navbar
  showClientPop: false,
  showProgrammePop: false,

  // Programme dashboard
  isDashboardContent: false,
  dashboardEditMode: false,
  _dashboardCtrl: null as DashboardController | null,

  // Manage access (which non-KAI users can view and edit the current programme)
  showAccessPop: false,
  accessEmails: [] as string[],
  newAccessEmail: '',
  accessSaving: false,
  accessError: null as string | null,

  async init() {
    const msalConfig = process.env.MICROSOFT_CLIENT_ID ? {
      auth: {
        clientId: process.env.MICROSOFT_CLIENT_ID!,
        authority: process.env.MICROSOFT_LOGIN_URL!,
        redirectUri: process.env.MICROSOFT_REDIRECT_URI || window.location.origin,
      },
    } : null;

    // If returning from a Microsoft redirect login, process the auth code first.
    const hasAuthCode = window.location.hash.includes('code=') || window.location.search.includes('code=');
    if (msalConfig && hasAuthCode) {
      try {
        const pca = new PublicClientApplication(msalConfig);
        await pca.initialize();
        this._msalPca = pca;
        const result = await pca.handleRedirectPromise();
        if (result?.idToken) {
          const response = await fetch('/api/auth/login-sso', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ idpToken: result.idToken }),
          });
          const data = await response.json();
          if (response.ok) {
            this.currentUser = data.user;
            await this.loadClients();
            this.initialLoading = false;
            return;
          }
          this.msalError = data.error || 'SSO login failed';
        }
      } catch (e) {
        console.error('SSO redirect error:', e);
      }
    } else if (msalConfig) {
      const pca = new PublicClientApplication(msalConfig);
      pca.initialize().then(() => { this._msalPca = pca; }).catch(() => { });
    }

    this.darkMode = localStorage.getItem('darkMode') === '1';
    try {
      await this.checkAuth();
      if (this.currentUser) {
        await this.loadClients();
      }
    } finally {
      this.initialLoading = false;
    }
  },

  toggleDarkMode() {
    this.darkMode = !this.darkMode;
    document.documentElement.classList.toggle('dark', this.darkMode);
    localStorage.setItem('darkMode', this.darkMode ? '1' : '0');
  },

  async checkAuth() {
    try {
      const response = await fetch('/api/auth/me', { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        this.currentUser = data.user;
      } else if (response.status === 401) {
        const data = await response.json().catch(() => ({}));
        if (data.sessionExpired) this.loginError = 'Your session has expired. Please log in again.';
      }
    } catch (err) {
      console.error('Auth check failed:', err);
    }
  },

  async handleLogin() {
    this.loggingIn = true;
    this.loginError = null;

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: this.loginEmail, password: this.loginPassword }),
      });

      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Login failed');

      if (data.mfaRequired) {
        this.mfaRequired = true;
        this.mfaSession = data.session;
        this.mfaMethod = data.mfaMethod;
        this.mfaEmail = data.email;
        this.loginPassword = '';
        return;
      }

      this.currentUser = data.user;
      this.loginEmail = '';
      this.loginPassword = '';
      await this.loadClients();
    } catch (err) {
      this.loginError = err instanceof Error ? err.message : 'Login failed';
    } finally {
      this.loggingIn = false;
    }
  },

  async handleMfaSubmit() {
    this.mfaLoading = true;
    this.mfaError = null;

    try {
      const response = await fetch('/api/auth/login-mfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: this.mfaEmail,
          session: this.mfaSession,
          type: this.mfaMethod,
          code: this.mfaCode,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Verification failed');

      this.currentUser = data.user;
      this.mfaRequired = false;
      this.mfaCode = '';
      this.mfaSession = '';
      await this.loadClients();
    } catch (err) {
      this.mfaError = err instanceof Error ? err.message : 'Verification failed';
    } finally {
      this.mfaLoading = false;
    }
  },

  async handleMicrosoftLogin() {
    this.msalLoading = true;
    this.msalError = null;

    const pca = this._msalPca;
    if (!pca) {
      this.msalError = 'Sign-in not ready yet, please try again.';
      this.msalLoading = false;
      return;
    }

    try {
      await pca.acquireTokenRedirect({ scopes: ['openid', 'email', 'profile'] });
      // Page navigates away — code below never runs
    } catch (err: any) {
      this.msalError = err instanceof Error ? err.message : 'Microsoft login failed';
      this.msalLoading = false;
    }
  },

  async handleLogout() {
    try {
      await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
    } catch (err) {
      console.error('Logout failed:', err);
    }
    this._resetSession();
  },

  // The server ends the session when the KAI token behind it has expired.
  _handleSessionExpired() {
    this._resetSession();
    this.loginError = 'Your session has expired. Please log in again.';
  },

  _resetSession() {
    this.currentUser = null;
    this.clients = [];
    this.dashboards = [];
    this.selectedDashboard = null;
    this.view = 'list';
    this.error = null;
    this.showAccessPop = false;
  },

  // --- Clients ---

  async loadClients() {
    this.clientsLoading = true;
    try {
      const response = await fetch('/api/clients', { credentials: 'include' });
      if (response.status === 401) return this._handleSessionExpired();
      if (!response.ok) throw new Error('Failed to load clients');
      const data = await response.json();
      this.clients = (data.clients as Client[]).slice().sort((a, b) => a.name.localeCompare(b.name));
      // Auto-select the first client and load its dashboards
      if (this.clients.length > 0) {
        this.selectedClientId = this.clients[0].id;
        await this.loadDashboards();
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load clients';
    } finally {
      this.clientsLoading = false;
    }
  },

  async onClientChange() {
    this.dashboards = [];
    this.selectedDashboard = null;
    this._dashboardCtrl = null;
    this.isDashboardContent = false;
    this.view = 'list';
    await this.loadDashboards();
  },

  selectedClientName() {
    return this.clients.find((c: Client) => c.id === this.selectedClientId)?.name ?? '';
  },

  // --- Dashboards ---

  async loadDashboards() {
    if (!this.selectedClientId) return;
    this.dashboardsLoading = true;
    this.error = null;
    try {
      const response = await fetch(`/api/dashboards?clientId=${this.selectedClientId}`, {
        credentials: 'include',
      });
      if (response.status === 401) return this._handleSessionExpired();
      if (!response.ok) throw new Error('Failed to load dashboards');
      const data = await response.json();
      this.dashboards = (data.dashboards as Dashboard[])
        .map((d: Dashboard) => this._normalizeDashboard(d))
        .sort((a, b) => a.programName.localeCompare(b.programName));
      if (this.dashboards.length > 0) {
        await this.openDashboard(this.dashboards[0]);
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to load dashboards';
    } finally {
      this.dashboardsLoading = false;
    }
  },

  async openDashboard(dashboard: Dashboard) {
    this.selectedDashboard = this._normalizeDashboard(dashboard);
    this.editMode = false;
    this.dashboardEditMode = false;

    const content = dashboard.content;
    const isEmpty = !content || (typeof content === 'object' && Object.keys(content as object).length === 0);
    this.isDashboardContent = isDashboardFormat(content) || isEmpty;
    this.view = 'detail';

    if (this.isDashboardContent) {
      await this.$nextTick();
      const initial = isDashboardFormat(content) ? content as DashboardContent : structuredClone(DEFAULT_DASHBOARD_CONTENT);
      try {
        this._initDashboard(initial);
      } catch (err) {
        // Content edited as raw JSON can pass the shape check yet still be
        // unrenderable — show it raw so it can be fixed, rather than breaking
        // the page for every viewer.
        console.error('Failed to render dashboard:', err);
        this._dashboardCtrl = null;
        this.isDashboardContent = false;
        this.error = 'This programme\'s content couldn\'t be shown as a dashboard, so it\'s shown as raw JSON. Edit it to fix the problem.';
      }
    }
  },

  _initDashboard(content: DashboardContent) {
    const self = this;
    // Pin the dashboard identity now — a retrying save must never follow
    // selectedDashboard onto a different dashboard.
    const { clientId, dashboardId } = this.selectedDashboard!;
    // Used only once selectedDashboard has moved to a different dashboard —
    // a queued/retrying save must still target this pinned one.
    let fallbackExpectedUpdatedAt = this.selectedDashboard!.updatedAt;
    // Set when a conflict can't be resolved in place — blocks saves until
    // an actual page reload.
    let blocked = false;
    this._dashboardCtrl = initDashboard('pd', content, {
      prefix: 'pd',
      clientId,
      editAllowed: true,
      onSessionExpired: () => self._handleSessionExpired(),
      onSave: async (data: DashboardContent) => {
        if (blocked) {
          throw new SaveError('This programme changed elsewhere — please reload the page to continue.', false);
        }

        // Snapshot to restore on a declined reload, so the header doesn't
        // show the other version while the editor body still shows this edit.
        const preSaveDashboard = self.selectedDashboard;

        const result = await self._updateDashboard(dashboardId, clientId, { content: data as unknown as Record<string, unknown> }, fallbackExpectedUpdatedAt);
        if (result.dashboard) fallbackExpectedUpdatedAt = result.dashboard.updatedAt;
        if (result.ok) return;

        if (result.status === 0) throw new SaveError(result.error, true);
        if (result.status === 401) throw new SaveError('Save failed — your session has expired. Please log in again.', false);
        if (result.status === 403) throw new SaveError('Save failed — you do not have edit access to this dashboard.', false);
        if (result.status === 404) throw new SaveError(result.error, false);
        if (result.status === 409) {
          if (result.dashboard && isDashboardFormat(result.dashboard.content)) {
            if (self._confirmReload()) {
              self._dashboardCtrl?.setData(result.dashboard.content);
              throw new SaveError('This programme was changed elsewhere and has been reloaded — your recent edit was not saved.', false);
            }
            if (preSaveDashboard) self._syncDashboard(preSaveDashboard);
            blocked = true;
            throw new SaveError('This programme was changed elsewhere. Your edit was not saved — reload the page when you\'re ready to continue.', false);
          }
          // Format changed too (e.g. saved as raw JSON) — can't reload in place.
          blocked = true;
          throw new SaveError('This programme changed format elsewhere — please reload the page to see the latest version.', false);
        }
        throw new SaveError(`Save failed (server error ${result.status}).`, result.status >= 500);
      },
    });
  },

  toggleDashboardEditMode() {
    if (!this._dashboardCtrl) return;
    this.dashboardEditMode = !this.dashboardEditMode;
    this._dashboardCtrl.setEditMode(this.dashboardEditMode);
  },

  goToList() {
    this.view = 'list';
    this.selectedDashboard = null;
    this.editMode = false;
    this.editError = null;
    this.isDashboardContent = false;
    this.dashboardEditMode = false;
    this._dashboardCtrl = null;
  },

  goToNew() {
    this.formProgramName = '';
    this.formClientId = this.selectedClientId;
    this.formError = null;
    this.view = 'new';
  },

  // --- Edit ---

  startEdit() {
    this.editProgramName = this.selectedDashboard!.programName;
    this.editContent = JSON.stringify(this.selectedDashboard!.content, null, 2);
    this.editError = null;
    this.editBlocked = false;
    this.editMode = true;
  },

  cancelEdit() {
    this.editMode = false;
    this.editError = null;
    this.editBlocked = false;
  },

  async renameProgram() {
    const current = this.selectedDashboard!.programName;
    const name = prompt('Programme name', current);
    if (!name || !name.trim() || name.trim() === current) return;
    const { clientId, dashboardId, updatedAt } = this.selectedDashboard!;
    const result = await this._updateDashboard(dashboardId, clientId, { programName: name.trim() }, updatedAt);
    if (!result.ok && result.status !== 401) alert(result.error);
  },

  // --- Manage access ---

  openAccessPop() {
    this.accessEmails = [...this.selectedDashboard!.allowedUserEmails];
    this.newAccessEmail = '';
    this.accessError = null;
    this.showAccessPop = true;
  },

  async addAccessUser() {
    const email = this.newAccessEmail.trim().toLowerCase();
    if (!email) return;
    if (this.accessEmails.some((e: string) => e.toLowerCase() === email)) { this.newAccessEmail = ''; return; }
    const ok = await this.saveAccessEmails([...this.accessEmails, email]);
    if (ok) this.newAccessEmail = '';
  },

  async removeAccessUser(email: string) {
    await this.saveAccessEmails(this.accessEmails.filter((e: string) => e !== email));
  },

  async saveAccessEmails(emails: string[]): Promise<boolean> {
    this.accessSaving = true;
    this.accessError = null;
    try {
      const { clientId, dashboardId, updatedAt } = this.selectedDashboard!;
      const result = await this._updateDashboard(dashboardId, clientId, { allowedUserEmails: emails }, updatedAt);

      // Success or 409 conflict alike, the response carries the dashboard's
      // current access list — reflect it so a retry applies cleanly instead
      // of clobbering whatever changed concurrently.
      if (result.dashboard) this.accessEmails = [...result.dashboard.allowedUserEmails];
      if (!result.ok) {
        this.accessError = result.error;
        return false;
      }
      return true;
    } finally {
      this.accessSaving = false;
    }
  },

  async saveEdit() {
    if (this.editBlocked) {
      this.editError = 'This programme changed elsewhere — please cancel and reopen the edit to continue.';
      return;
    }

    this.editSaving = true;
    this.editError = null;

    let content: Record<string, unknown>;
    try {
      content = JSON.parse(this.editContent);
    } catch {
      this.editError = 'Invalid JSON — please fix before saving.';
      this.editSaving = false;
      return;
    }

    // Snapshot to restore on a declined reload — see the visual editor's onSave.
    const preSaveDashboard = this.selectedDashboard;
    try {
      const { clientId, dashboardId, updatedAt } = this.selectedDashboard!;
      const result = await this._updateDashboard(dashboardId, clientId, { programName: this.editProgramName, content }, updatedAt);

      if (result.ok) {
        this.editMode = false;
      } else if (result.status === 409 && result.dashboard) {
        if (this._confirmReload()) {
          this.editProgramName = result.dashboard.programName;
          this.editContent = JSON.stringify(result.dashboard.content, null, 2);
          this.editError = 'This programme was changed elsewhere and has been reloaded — your recent edit was not saved.';
        } else {
          if (preSaveDashboard) this._syncDashboard(preSaveDashboard);
          this.editBlocked = true;
          this.editError = 'This programme was changed elsewhere. Your edit was not saved — reload the page when you\'re ready to continue.';
        }
      } else {
        this.editError = result.error;
      }
    } finally {
      this.editSaving = false;
    }
  },

  // --- Delete ---

  async deleteDashboard() {
    if (!confirm(`Delete "${this.selectedDashboard!.programName}"? This cannot be undone.`)) return;

    try {
      const response = await fetch(
        `/api/dashboards/${this.selectedDashboard!.dashboardId}?clientId=${this.selectedDashboard!.clientId}`,
        { method: 'DELETE', credentials: 'include' },
      );

      if (response.status === 401) return this._handleSessionExpired();
      if (!response.ok && response.status !== 204) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete');
      }

      const deletedId = this.selectedDashboard!.dashboardId;
      this.dashboards = this.dashboards.filter((d: Dashboard) => d.dashboardId !== deletedId);
      this.selectedDashboard = null;
      this._dashboardCtrl = null;
      this.isDashboardContent = false;
      this.dashboardEditMode = false;
      if (this.dashboards.length > 0) {
        await this.openDashboard(this.dashboards[0]);
      } else {
        this.view = 'list';
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : 'Failed to delete dashboard';
    }
  },

  // --- Create ---

  async createDashboard() {
    this.formSaving = true;
    this.formError = null;

    try {
      const response = await fetch('/api/dashboards', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          clientId: this.formClientId,
          programName: this.formProgramName,
          content: DEFAULT_DASHBOARD_CONTENT,
        }),
      });

      if (response.status === 401) return this._handleSessionExpired();
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to create dashboard');

      this.dashboards.unshift(data.dashboard);
      await this.openDashboard(data.dashboard);
    } catch (err) {
      this.formError = err instanceof Error ? err.message : 'Failed to create dashboard';
    } finally {
      this.formSaving = false;
    }
  },

  // --- Helpers ---

  // Older rows predate allowedUserEmails, so the API can return it undefined.
  _normalizeDashboard(dashboard: Dashboard): Dashboard {
    return dashboard.allowedUserEmails ? dashboard : { ...dashboard, allowedUserEmails: [] };
  },

  // Applies a dashboard from the API to local state — a 409 response carries
  // the current dashboard too, so this covers both success and conflict.
  _syncDashboard(dashboard: Dashboard): Dashboard {
    const normalized = this._normalizeDashboard(dashboard);
    if (this.selectedDashboard?.dashboardId === normalized.dashboardId) {
      this.selectedDashboard = normalized;
    }
    const idx = this.dashboards.findIndex((d: Dashboard) => d.dashboardId === normalized.dashboardId);
    if (idx !== -1) this.dashboards[idx] = normalized;
    return normalized;
  },

  // Single entry point for every PUT /dashboards/:id call, so expectedUpdatedAt
  // is always sourced from the live selectedDashboard — a rename and an
  // access-list edit on the same open dashboard each see the other's update.
  async _updateDashboard(
    dashboardId: string,
    clientId: string,
    body: DashboardUpdateBody,
    fallbackExpectedUpdatedAt: string,
  ): Promise<DashboardUpdateResult> {
    const expectedUpdatedAt = this.selectedDashboard?.dashboardId === dashboardId
      ? this.selectedDashboard.updatedAt
      : fallbackExpectedUpdatedAt;

    let response: Response;
    try {
      response = await fetch(`/api/dashboards/${dashboardId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ clientId, ...body, expectedUpdatedAt }),
      });
    } catch {
      return { ok: false, status: 0, error: 'Save failed — network error.', dashboard: null };
    }

    // Covers every caller — rename, access list, raw and visual editors.
    if (response.status === 401) {
      this._handleSessionExpired();
      return { ok: false, status: 401, error: 'Your session has expired. Please log in again.', dashboard: null };
    }

    let data: { dashboard?: Dashboard; error?: string } = {};
    try {
      data = await response.json();
    } catch {
      // Tolerated below, but a successful write still needs its updatedAt
      // for the next check — handled by the refetch below.
    }

    let dashboard = data.dashboard ? this._syncDashboard(data.dashboard) : null;

    if (response.ok && !dashboard) {
      try {
        const refetch = await fetch(`/api/dashboards/${dashboardId}?clientId=${clientId}`, { credentials: 'include' });
        if (refetch.ok) {
          const refetchData = await refetch.json();
          if (refetchData.dashboard) dashboard = this._syncDashboard(refetchData.dashboard);
        }
      } catch {
        // Best-effort — a future conflict, if any, will self-correct anyway.
      }
    }

    if (!response.ok) {
      return { ok: false, status: response.status, error: data.error || 'Failed to save.', dashboard };
    }
    return { ok: true, dashboard };
  },

  _confirmReload(): boolean {
    return confirm(
      'This programme was changed by someone else. Reloading will discard your unsaved changes and show the latest version.\n\nReload now?',
    );
  },

  prettyContent(dashboard: Dashboard) {
    try {
      return JSON.stringify(dashboard.content, null, 2);
    } catch {
      return String(dashboard.content);
    }
  },

  formatDate(iso: string) {
    return new Date(iso).toLocaleDateString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  },
}));

Alpine.start();
